// Yield the current frame before running a route-commit navigation, so the destination's React render lands on
// a LATER task instead of the frame the slide settles on.
//
// The commit — router.navigate() into the live <Outlet/> — is the one piece of the transition that cannot leave
// the main thread (see README §4). With the route's chunk + loader data already preloaded on pointer-down it
// runs SYNCHRONOUSLY, so issued straight from `finish()` it lands on the very frame the spring arrives: it
// blocks the compositor hand-off and freezes input for the length of that render right as the page appears. But
// the incoming pane already shows the live destination and is still covering the viewport, so there is nothing
// to see for a beat — we can hand the commit to a lower-priority task and let the settle's final frame paint and
// input stay responsive. It commits under the covering pane a task later; teardown (which waits on the live
// layer catching up) then reveals it, exactly as before. See README §5.A.
//
// scheduler.postTask where available — a real browser priority ('user-visible' runs promptly, but yields to
// 'user-blocking' input, so a tap during the commit is handled first). Feature-detected: this app still ships to
// browsers without postTask (the same old-browser constraint that bans `.at()`/`toSorted`), where a macrotask
// via setTimeout yields the frame just the same.

interface PostTaskScheduler {
  postTask?: (callback: () => void, options?: { priority?: 'user-blocking' | 'user-visible' | 'background' }) => unknown
}

/** Run `commit` on a task AFTER the current frame; resolves when its navigation settles (so a caller can keep an
 *  in-flight flag up across it, e.g. the controller's internalNav guard). */
export function deferRouteCommit(commit: () => unknown): Promise<unknown> {
  return new Promise((resolve, reject) => {
    const run = () => Promise.resolve().then(commit).then(resolve, reject)
    const scheduler = (globalThis as { scheduler?: PostTaskScheduler }).scheduler
    if (scheduler && typeof scheduler.postTask === 'function') {
      // postTask only rejects if the task is aborted; we never pass a signal, so it always runs.
      void Promise.resolve(scheduler.postTask(run, { priority: 'user-visible' })).catch(() => {})
    } else {
      setTimeout(run, 0)
    }
  })
}
